<template>
    <q-card
        :class="$q.screen.gt.lg ? 'dialog__lg' : 'dialog__xs'"
        style="z-index: 99999 !important"
    >
        <!-- Dialog header -->
        <q-bar class="bg-primary text-white">
            {{ $t('Downtime events') }}
            <q-space />
            <q-btn @click="close" class="text-white" icon="close" dense flat>
                <q-tooltip>{{ $t('Close') }}</q-tooltip>
            </q-btn>
        </q-bar>

        <div class="row q-mr-md q-ml-md q-mt-md">
            <div class="col-12 text-grey-9 text-h6">
                <div class="text-h6" v-if="getAsset(eventsData.assetId)">
                    {{ getAsset(eventsData.assetId)['name'] }}
                </div>
                <div class="text-h6" v-else>
                    {{ eventsData.productName }}
                </div>
                <div class="text-subtitle2">
                    {{ eventsData.name }}
                </div>
                <div class="text-grey-9 text-subtitle2">
                    {{ formatDate(eventsData.start) }} -
                    {{ formatDate(eventsData.end) }}
                </div>
            </div>
        </div>

        <!-- Downtime Event Table -->
        <q-card-section>
            <q-table
                @request="throttledOnRequest"
                v-model:pagination="table.pagination"
                :rows="table.data"
                :columns="table.columns"
                :rows-per-page-options="[table.pagination.rowsPerPage]"
                :loading="table.isLoading"
                :no-data-label="$t('No downtime event available')"
                :grid="$q.screen.lt.lg"
                table-style="min-height: 30vh"
                row-key="id"
                binary-state-sort
                flat
                bordered
            >
                <!-- Status column -->
                <template v-slot:body-cell-status="props">
                    <q-td :props="props">
                        <div>
                            <q-badge
                                class="phase-table-status"
                                :color="
                                    ASSET_STATUS_TO_STYLE[props.row.phase][
                                        'color'
                                    ]
                                "
                                :label="
                                    $t(
                                        ASSET_STATUS_TO_STYLE[props.row.phase][
                                            'label'
                                        ]
                                    )
                                "
                            >
                            </q-badge>
                        </div>
                    </q-td>
                </template>

                <!-- Description column (render description as saved, with newlines, etc.) -->
                <template v-slot:body-cell-description="props">
                    <q-td :props="props">
                        <div>
                            {{ props.row.description }}
                        </div>
                    </q-td>
                </template>

                <!-- Cause column -->
                <template v-slot:body-cell-cause__name="props">
                    <q-td :props="props">
                        <div v-if="props.row.cause_name">
                            {{ props.row.cause_name }}
                        </div>
                        <div v-else>
                            <missing-cause-label
                                v-model="causeSelectProps.selection"
                                :phaseId="props.row.id"
                                :phaseUUID="props.row.uuid"
                                :phaseEnd="props.row.end"
                                @refreshList="refreshList()"
                                @clearSelection="
                                    causeSelectProps.selection = null
                                "
                                @causeJustified="$emit('causeJustified')"
                                :baseURL="causeSelectProps.url"
                                :queryParams="causeSelectProps.queryParams"
                                :label="causeSelectProps.label"
                                :dialogTitle="causeSelectProps.dialogTitle"
                                :searchLabel="causeSelectProps.searchLabel"
                                :isDense="true"
                                :isFlat="true"
                            >
                                <template v-slot:item="data">
                                    <cause-card
                                        @select="
                                            select(
                                                data.props.row,
                                                causeSelectProps
                                            )
                                        "
                                        :name="data.props.row.name"
                                        :lossType="data.props.row.type_of_loss"
                                        :categoryName="
                                            data.props.row.category_name
                                        "
                                        :planned="data.props.row.planned"
                                        :description="
                                            data.props.row.description
                                        "
                                        :targetDowntime="
                                            data.props.row.target_down_time
                                        "
                                        :isSelected="
                                            causeSelectProps.selection &&
                                            causeSelectProps.selection.value ===
                                                data.props.row.id
                                        "
                                        :isSelectable="true"
                                        :equipmentFailure="
                                            data.props.row.equipment_failure
                                        "
                                    >
                                    </cause-card>
                                </template>
                            </missing-cause-label>
                        </div>
                    </q-td>
                </template>

                <!-- Card template for small devices -->
                <template v-slot:item="props">
                    <div class="full-width q-pa-xs">
                        <q-card class="full-width full-height">
                            <q-card-section>
                                <div class="row">
                                    <!-- Fields column (breaks down to 2 columns on medium devices, single column on small devices) -->
                                    <div class="col-12">
                                        <div class="row">
                                            <!-- First column of fields -->
                                            <div
                                                class="col-xs-12 col-md-6 full-height"
                                            >
                                                <q-chip
                                                    :color="
                                                        ASSET_STATUS_TO_STYLE[
                                                            props.row.phase
                                                        ]['color']
                                                    "
                                                    :label="
                                                        $t(
                                                            ASSET_STATUS_TO_STYLE[
                                                                props.row.phase
                                                            ]['label']
                                                        )
                                                    "
                                                    class="q-mx-none q-mb-sm text-uppercase"
                                                    text-color="white"
                                                    style="font-weight: 500"
                                                    square
                                                >
                                                </q-chip>

                                                <!-- Date range field -->
                                                <div
                                                    class="row"
                                                    :style="
                                                        $q.screen.lt.sm
                                                            ? 'font-size: 11.5px;'
                                                            : ''
                                                    "
                                                >
                                                    <q-icon
                                                        name="far fa-calendar-alt"
                                                        class="q-mr-xs"
                                                        size="sm"
                                                        color="grey"
                                                    >
                                                        <q-tooltip
                                                            anchor="bottom middle"
                                                            self="top middle"
                                                        >
                                                            {{
                                                                $t('Date range')
                                                            }}
                                                        </q-tooltip>
                                                    </q-icon>
                                                    <div
                                                        v-if="
                                                            props.row.end ===
                                                            null
                                                        "
                                                        class="q-ml-xs"
                                                    >
                                                        {{
                                                            `${formatDate(
                                                                props.row.start
                                                            )} (${$t(
                                                                'Ongoing'
                                                            )})`
                                                        }}
                                                    </div>
                                                    <div
                                                        v-else
                                                        class="q-ml-xs"
                                                        style="margin-top: 2px"
                                                    >
                                                        {{
                                                            `${formatDate(
                                                                props.row.start
                                                            )} - ${formatDate(
                                                                props.row.end
                                                            )}`
                                                        }}
                                                    </div>
                                                </div>
                                                <!-- Duration field -->
                                                <div class="q-mt-xs">
                                                    <q-icon
                                                        name="far fa-clock"
                                                        class="q-mr-xs"
                                                        size="sm"
                                                        color="grey"
                                                    >
                                                        <q-tooltip
                                                            anchor="bottom middle"
                                                            self="top middle"
                                                        >
                                                            {{ $t('Duration') }}
                                                        </q-tooltip>
                                                    </q-icon>
                                                    {{
                                                        convertDurationToDHMS(
                                                            props.row.duration_s
                                                        )
                                                    }}
                                                </div>
                                            </div>

                                            <!-- Second column of fields -->
                                            <div
                                                class="col-xs-12 col-md-6 full-height"
                                            >
                                                <div
                                                    v-if="props.row.asset"
                                                    class="q-mt-xs"
                                                >
                                                    <q-icon
                                                        name="far fa-industry-alt"
                                                        class="q-mr-xs"
                                                        size="sm"
                                                        color="grey"
                                                    >
                                                        <q-tooltip
                                                            anchor="bottom middle"
                                                            self="top middle"
                                                        >
                                                            {{ $t('Asset') }}
                                                        </q-tooltip>
                                                    </q-icon>
                                                    {{ props.row.asset_name }}
                                                </div>
                                                <div
                                                    v-if="props.row.product"
                                                    class="q-mt-xs"
                                                >
                                                    <q-icon
                                                        name="far fa-box"
                                                        class="q-mr-xs"
                                                        size="sm"
                                                        color="grey"
                                                    >
                                                        <q-tooltip
                                                            anchor="bottom middle"
                                                            self="top middle"
                                                        >
                                                            {{ $t('Product') }}
                                                        </q-tooltip>
                                                    </q-icon>
                                                    {{ props.row.product_name }}
                                                </div>
                                                <div
                                                    v-if="props.row.cause"
                                                    class="q-my-xs"
                                                >
                                                    <q-icon
                                                        name="far fa-exclamation-circle"
                                                        class="q-mr-xs"
                                                        size="sm"
                                                        color="grey"
                                                    >
                                                        <q-tooltip
                                                            anchor="bottom middle"
                                                            self="top middle"
                                                        >
                                                            {{ $t('Cause') }}
                                                        </q-tooltip>
                                                    </q-icon>
                                                    {{ props.row.cause_name }}
                                                </div>
                                                <div v-else>
                                                    <missing-cause-label
                                                        v-model="
                                                            causeSelectProps.selection
                                                        "
                                                        :assetId="
                                                            props.row.asset
                                                        "
                                                        :phaseId="props.row.id"
                                                        :phaseEnd="
                                                            props.row.end
                                                        "
                                                        :phaseUUID="
                                                            props.row.uuid
                                                        "
                                                        @refreshList="
                                                            refreshList()
                                                        "
                                                        @clearSelection="
                                                            causeSelectProps.selection =
                                                                null
                                                        "
                                                        @causeJustified="
                                                            $emit(
                                                                'causeJustified'
                                                            )
                                                        "
                                                        :baseURL="
                                                            causeSelectProps.url
                                                        "
                                                        :queryParams="
                                                            causeSelectProps.queryParams
                                                        "
                                                        :label="
                                                            causeSelectProps.label
                                                        "
                                                        :dialogTitle="
                                                            causeSelectProps.dialogTitle
                                                        "
                                                        :searchLabel="
                                                            causeSelectProps.searchLabel
                                                        "
                                                        :isDense="true"
                                                        :isFlat="true"
                                                    >
                                                        <template
                                                            v-slot:item="data"
                                                        >
                                                            <cause-card
                                                                @select="
                                                                    select(
                                                                        data
                                                                            .props
                                                                            .row,
                                                                        causeSelectProps
                                                                    )
                                                                "
                                                                :name="
                                                                    data.props
                                                                        .row
                                                                        .name
                                                                "
                                                                :lossType="
                                                                    data.props
                                                                        .row
                                                                        .type_of_loss
                                                                "
                                                                :categoryName="
                                                                    data.props
                                                                        .row
                                                                        .category_name
                                                                "
                                                                :planned="
                                                                    data.props
                                                                        .row
                                                                        .planned
                                                                "
                                                                :description="
                                                                    data.props
                                                                        .row
                                                                        .description
                                                                "
                                                                :targetDowntime="
                                                                    data.props
                                                                        .row
                                                                        .target_down_time
                                                                "
                                                                :isSelected="
                                                                    causeSelectProps.selection &&
                                                                    causeSelectProps
                                                                        .selection
                                                                        .value ===
                                                                        data
                                                                            .props
                                                                            .row
                                                                            .id
                                                                "
                                                                :isSelectable="
                                                                    true
                                                                "
                                                                :equipmentFailure="
                                                                    data.props
                                                                        .row
                                                                        .equipment_failure
                                                                "
                                                            >
                                                            </cause-card>
                                                        </template>
                                                    </missing-cause-label>
                                                </div>
                                            </div>
                                            <div
                                                v-if="
                                                    props.row.description &&
                                                    props.row.description !== ''
                                                "
                                                class="q-mt-xs"
                                            >
                                                <q-icon
                                                    name="far fa-file-alt"
                                                    class="q-mr-xs"
                                                    size="sm"
                                                    color="grey"
                                                >
                                                    <q-tooltip
                                                        anchor="bottom middle"
                                                        self="top middle"
                                                    >
                                                        {{ $t('Description') }}
                                                    </q-tooltip>
                                                </q-icon>
                                                {{ props.row.description }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </q-card-section>
                        </q-card>
                    </div>
                </template>
            </q-table>
        </q-card-section>

        <!-- Dialog actions -->
        <q-card-section class="float-right q-pt-none">
            <q-btn
                class="q-mr-md"
                color="white"
                text-color="primary"
                @click="close"
                outline
                >{{ $t('Close') }}</q-btn
            >
        </q-card-section>
    </q-card>
</template>

<script>
import UpdateURL from '@mixins/UpdateURL.vue';
import { ASSET_STATUS_TO_STYLE } from '@models/assets.js';
import { throttle } from 'quasar';
import { getLocaleFormat } from '@models/timeformats.js';
import { convertDurationToDHMS } from '@utils/time.js';
import MissingCauseLabel from '@components/MissingCauseLabel.vue';
import CauseCard from '@components/CauseCard.vue';
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';
import { useAssetsStore } from '@stores/assets';

export default {
    name: 'EventsTable',
    components: {
        MissingCauseLabel,
        CauseCard
    },
    mixins: [UpdateURL],
    emits: ['causeJustified', 'close'],
    props: {
        eventsData: {
            type: Object,
            required: true
        }
    },
    created() {
        // add throttling to the getDeviceStatus function to prevent users
        // from triggering too many requests
        this.throttledOnRequest = throttle(this.onRequest, 200);
    },
    mounted() {
        // load event list
        this.onRequest({
            pagination: this.table.pagination
        });
    },
    data() {
        return {
            // models
            ASSET_STATUS_TO_STYLE,

            // base url
            baseURL: 'events/',

            // event table
            table: {
                data: [],
                columns: [
                    {
                        name: 'asset__name',
                        field: 'asset_name',
                        label: this.$t('Asset'),
                        required: true,
                        align: 'left',
                        sortable: true,
                        classes: 'phase-table-cell'
                    },
                    {
                        name: 'status',
                        field: 'phase',
                        label: this.$t('Status'),
                        required: true,
                        align: 'left',
                        sortable: false,
                        classes: 'phase-table-cell'
                    },
                    {
                        name: 'start',
                        field: 'start',
                        label: this.$t('Start Time'),
                        align: 'left',
                        sortable: true,
                        format: val => this.formatDate(val)
                    },
                    {
                        name: 'end',
                        field: 'end',
                        label: this.$t('End Time'),
                        align: 'left',
                        sortable: true,
                        format: val => this.formatDate(val)
                    },
                    {
                        name: 'duration_s',
                        field: 'duration_s',
                        label: this.$t('Duration'),
                        required: true,
                        align: 'right',
                        sortable: true,
                        classes: 'phase-table-cell',
                        format(val) {
                            return convertDurationToDHMS(val);
                        }
                    },
                    {
                        name: 'product__name',
                        field: 'product_name',
                        label: this.$t('Product'),
                        required: true,
                        align: 'left',
                        sortable: true,
                        classes: 'phase-table-cell'
                    },
                    {
                        name: 'cause__name',
                        field: 'cause_name',
                        label: this.$t('Cause'),
                        align: 'left'
                    },
                    {
                        name: 'description',
                        field: 'description',
                        label: this.$t('Description'),
                        align: 'left',
                        style:
                            // wrap text overflow to next line
                            'max-width: 500px; white-space: initial; word-wrap: break-word;'
                    }
                    // {
                    //     name: 'actions',
                    //     field: 'actions',
                    //     label: this.$t('Actions'),
                    //     align: 'left'
                    // }
                ],
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                },
                isLoading: false
            },
            // selector for a cause
            causeSelectProps: {
                url: `assets/${this.eventsData.assetId}/causes/`,
                queryParams: { ordering: 'name' },
                label: this.$t('Cause'),
                dialogTitle: this.$t('Select a cause'),
                searchLabel: this.$t('Search cause'),
                selection: null
            }
        };
    },
    computed: {
        ...mapState(useAssetsStore, {
            getAsset: 'getAsset'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Get URL with query parameters.
         * @return {String}
         */
        url() {
            // get current pagination state attributes
            const { descending, page, sortBy } = this.table.pagination;

            const asset = this.eventsData.assetId ?? undefined;
            const product = this.eventsData.productId ?? undefined;

            var queryParams = {
                start__lt: this.eventsData.end,
                end__gt: this.eventsData.start,
                ordering: descending ? `-${sortBy}` : `${sortBy}`,
                page: page
            };
            if (this.eventsData.assetId && this.eventsData.productId) {
                queryParams['asset'] = asset;
                queryParams['product'] = product;
            } else if (this.eventsData.assetId && !this.eventsData.productId) {
                queryParams['asset'] = asset;
            } else if (this.eventsData.productId) {
                queryParams['product'] = product;
            }

            if (this.eventsData.groupBy === 'causes') {
                if (this.eventsData.id !== null) {
                    queryParams['cause'] = this.eventsData.id;
                } else {
                    queryParams['has_cause'] = false;
                }
            }

            // for categories
            if (this.eventsData.groupBy === 'categories') {
                if (this.eventsData.id !== null && this.eventsData.id > 0) {
                    queryParams['cause__category'] = this.eventsData.id;
                } else {
                    queryParams['has_category'] = false;
                }
            }

            if (this.eventsData.isMinor !== null) {
                queryParams['is_major'] = !this.eventsData.isMinor;
            }

            if (this.eventsData.typeOfLoss !== null) {
                queryParams['type_of_loss'] = this.eventsData.typeOfLoss;
            }

            if (this.eventsData.planned !== null) {
                queryParams['planned'] = this.eventsData.planned;
            }

            return this.updateURL({
                baseURL: this.baseURL,
                params: queryParams
            });
        }
    },
    methods: {
        convertDurationToDHMS,
        /**
         * Close dialog.
         */
        close() {
            this.$emit('close');
        },
        /**
         * Format date.
         * @param {String} datetime
         * @return {String} Formatted date
         */
        formatDate(datetime) {
            return getLocaleFormat(datetime, this.userLocale);
        },
        /**
         * Handle requesting data from the server. Triggered by any table sort/search/page changes.
         * @param {Object} props
         */
        async onRequest(props) {
            // begin loading
            this.table.isLoading = true;

            // update pagination object
            this.table.pagination = props.pagination;

            // get events
            await this.getEvents();
        },
        /**
         * Fetch events from the server.
         */
        async getEvents() {
            await this.$axios
                .get(this.url)
                .then(response => {
                    // push data
                    this.table.data = response.data.results;
                    // update total events count
                    this.table.pagination.rowsNumber = response.data.count;
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t(
                            'Failed to load events. Please try again later'
                        ),
                        error
                    );
                })
                .finally(() => {
                    this.table.isLoading = false;
                });
        },
        /**
         * Refetch the results from the server.
         */
        async refreshList() {
            // fetch data from the server
            await this.onRequest({
                pagination: this.table.pagination,
                filter: ''
            });
        },
        /**
         * Select/ deselect cause and products in table-select component
         * @param {Object} row - Event from table row.
         * @param {Object} props - Table selection props (causeTableSelect, productTableSelect, MissingCauseLabel)
         */
        select(row, props) {
            if (props.selection && props.selection.value === row.id) {
                props.selection = null;
            } else {
                props.selection = {
                    label: row.name,
                    value: row.id
                };
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.dialog__lg {
    width: 80%;
    max-width: none;
}

.dialog__xs {
    width: 100%;
    max-width: none;
}

.phase-table-actions-col {
    width: 100px;
}

.phase-table-button {
    padding: 6px;
    font-size: 12px;
}
</style>
